// react
import { useLayoutEffect, useState } from 'react';

// router
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';

// mui
import {
	alpha,
	Box,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import { ChevronLeft as IconCollapse, ChevronRight as IconExpand } from '@mui/icons-material';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { Project } from '../../../@types';

// navigation constants
import {
	NAV_TOP_LEVEL_ITEMS,
	NAV_BOTTOM_LEVEL_ITEMS,
	NAV_GROUPS,
	buildNavItemPath,
	getCurrentNavValue,
	NavMenuItem,
} from '../../../data/constants';

interface ProjectSidebarProps {
	project: Project;
}

export const ProjectSidebar = ({ project }: ProjectSidebarProps) => {
	const location = useLocation();
	const theme = useTheme();
	const { t } = useTranslation();

	const currentTab = getCurrentNavValue(location.pathname);

	// Sidebar state
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
	const sidebarExpandedWidth = 238;
	const sidebarCollapsedWidth = 60;
	const sidebarWidth = isSidebarCollapsed ? sidebarCollapsedWidth : sidebarExpandedWidth;

	// Scroll the page (window) to top on tab change. The shell uses the
	// window as its single scroll container — sidebar is sticky/full-height,
	// content flows naturally — so route changes need to reset window scroll.
	useLayoutEffect(() => {
		window.scrollTo({ top: 0, behavior: 'auto' });
	}, [currentTab]);

	const renderNavItem = (item: NavMenuItem) => {
		const isActive = currentTab === item.value;
		const IconComponent = item.icon;
		const itemPath = buildNavItemPath(item, project?.idWbg || '');
		const label = t(item.labelKey);

		const button = (
			<ListItemButton
				component={RouterLink}
				to={itemPath}
				selected={isActive}
				sx={{
					borderRadius: 2,
					mb: 0.5,
					py: 1,
					px: isSidebarCollapsed ? 1.5 : 2,
					minHeight: 48,
					justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
					'&.Mui-selected': {
						bgcolor: alpha(theme.palette.primary.main, 0.1),
						'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
						'& .MuiListItemIcon-root': { color: theme.palette.primary.main },
						'& .MuiListItemText-primary': {
							color: theme.palette.primary.main,
							fontWeight: 600,
						},
					},
					'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) },
				}}>
				<ListItemIcon sx={{ minWidth: isSidebarCollapsed ? 'auto' : 44 }}>
					<IconComponent sx={{ fontSize: 24 }} />
				</ListItemIcon>
				{!isSidebarCollapsed && (
					<ListItemText
						primary={label}
						primaryTypographyProps={{
							fontSize: '1.35rem',
							fontWeight: isActive ? 600 : 500,
							whiteSpace: 'nowrap',
						}}
					/>
				)}
			</ListItemButton>
		);

		return isSidebarCollapsed ? (
			<Tooltip key={item.value} title={label} placement="right" arrow>
				{button}
			</Tooltip>
		) : (
			<Box key={item.value}>{button}</Box>
		);
	};

	return (
		<Box
			sx={(theme) => ({
				display: 'flex',
				alignItems: 'flex-start', // prevent flex from stretching the sticky sidebar
				bgcolor: 'background.default',
				borderTop: '1px solid',
				borderColor: 'divider',
				// Cancel the global `<main>` bottom padding (Authenticated.tsx) so the
				// sidebar can extend flush to the bottom of the viewport. The content
				// column re-adds its own bottom padding below for breathing room.
				marginBottom: theme.spacing(-3),
			})}>
			{/* Sidebar — sticky, full viewport height. The window is the only scroller. */}
			<Box
				sx={{
					width: sidebarWidth,
					flexShrink: 0,
					bgcolor: 'background.paper',
					borderRight: '1px solid',
					borderColor: 'divider',
					display: 'flex',
					flexDirection: 'column',
					transition: 'width 0.2s ease-in-out',
					position: 'sticky',
					// The app `<Header>` is `position="static"` (see ui/Header/Header.tsx),
					// so it scrolls away with the document. Sticking the sidebar at
					// `top: 0` lets it slide up to the viewport top once the header is
					// gone, instead of leaving a `headerHeight` white gap above it.
					top: 0,
					height: '100vh',
					alignSelf: 'flex-start',
					overflow: 'visible', // keep so the floating collapse chevron can poke out
				}}>
				{/* Collapse toggle (floating on right edge) */}
				<IconButton
					onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
					size="small"
					sx={{
						position: 'absolute',
						top: 16,
						right: -14,
						zIndex: 2,
						width: 28,
						height: 28,
						bgcolor: 'background.paper',
						border: '1px solid',
						borderColor: 'divider',
						boxShadow: 1,
						'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
					}}>
					{isSidebarCollapsed ? (
						<IconExpand sx={{ fontSize: 16, color: theme.palette.primary.main }} />
					) : (
						<IconCollapse sx={{ fontSize: 16, color: theme.palette.primary.main }} />
					)}
				</IconButton>

				{/* Navigation */}
				<Box sx={{ flex: 1, py: 2, px: isSidebarCollapsed ? 1 : 2, overflowY: 'auto' }}>
					<List disablePadding>{NAV_TOP_LEVEL_ITEMS.map(renderNavItem)}</List>

					{NAV_GROUPS.map((group) => (
						<Box key={group.id} sx={{ mt: 2 }}>
							{!isSidebarCollapsed && (
								<Typography
									variant="overline"
									sx={{
										px: 2,
										py: 0.5,
										display: 'block',
										color: 'text.secondary',
										fontSize: '1.05rem',
										fontWeight: 600,
										letterSpacing: 1.2,
										whiteSpace: 'nowrap',
									}}>
									{t(group.labelKey)}
								</Typography>
							)}
							{isSidebarCollapsed && <Box sx={{ height: 1, bgcolor: 'divider', mx: 1, my: 1 }} />}
							<List disablePadding>{group.items.map(renderNavItem)}</List>
						</Box>
					))}

					{NAV_BOTTOM_LEVEL_ITEMS.length > 0 && (
						<Box sx={{ mt: 2 }}>
							{!isSidebarCollapsed && <Box sx={{ height: 1, bgcolor: 'divider', mx: 1, mb: 1 }} />}
							{isSidebarCollapsed && <Box sx={{ height: 1, bgcolor: 'divider', mx: 1, my: 1 }} />}
							<List disablePadding>{NAV_BOTTOM_LEVEL_ITEMS.map(renderNavItem)}</List>
						</Box>
					)}
				</Box>
			</Box>

			{/* Content — no fixed height, no inner overflow. Expands down the page;
			    window handles all scrolling. */}
			<Box
				sx={(theme) => ({
					flex: 1,
					minWidth: 0,
					display: 'flex',
					flexDirection: 'column',
					// Breathing room at the bottom of routed content (replaces the
					// global `<main>` padding that we cancelled on the shell above).
					paddingBottom: theme.spacing(3),
				})}>
				<Outlet />
			</Box>
		</Box>
	);
};
